import { useState } from 'react';
import { Input, InputNumber, Segmented, Select } from 'antd';

import PatternEditor from './PatternEditor';
import { formatLimit } from '../../utils/elementFormat/text';
import {
	CASE_CHOICES, CASE_HINTS, MAX_MESSAGE, PRESETS, checkPattern, matchesPattern, maxLengthCeiling,
	maxLengthRangeText, patternChoiceOf, patternRefusalText, presetOf, resolvePattern, storedTextValue,
} from '../../utils/textRules';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * The sub-editor for a text element's rules (docs/plans/data-types-second-
 * pass.md §3.4, PR 2; decision D14 and the owner's note on it), the mould of
 * MeasurementOptions — one file per type, a section inside the element's row:
 *
 *   Maximum length   text-small / text-large: the element's own limit, 1 to
 *                    the type's ceiling (255; 20,000, or 50,000 with "Allow
 *                    longer text"). Empty: the ceiling. The item form shows a
 *                    count against it.
 *   Case             text-small: As typed · Capitals · Small letters — how
 *                    the value is STORED.
 *   Pattern          text-small: None, one of the presets (⚑ the owner asked
 *                    for predefined patterns beside a custom one), or a custom
 *                    pattern with the one sentence a person reads when a value
 *                    does not fit. The whole value must match. A custom
 *                    pattern is built as pieces, or from examples, without
 *                    writing a regular expression (PatternEditor, KPR-956);
 *                    the regex it stores is behind "Edit as expression".
 *                    "Letters in either case" is the `i` flag. A custom
 *                    pattern is checked here as it is built with the rule the
 *                    API applies (utils/elementFormat/pattern.js, vendored);
 *                    the API adds a timed probe the browser cannot run, and
 *                    its refusal (`error`) lands under the control it names.
 *   Try a value      text-small with a pattern: what a value would be saved as
 *                    and whether it matches, in words — nothing is stored.
 *
 * "Trim spaces" is a boolean and rides the toggle run with every other switch
 * (ELEMENT_OPTION_TOGGLES). Used by the card element editor (large controls,
 * path-based onChange) and the multi add editor's ad hoc inputs (compact); both
 * hand in `onPatch(partial options)`. Clearing writes `undefined`, so the key
 * serializes away. A text element whose legacy `choices` make it a choice
 * picker gets no rules panel — the parent does not render this.
 */
export default function TextOptions({ idPrefix, dataType, options = {}, onPatch, error = null, compact = false }) {
	const [trial, setTrial] = useState('');
	const size = compact ? 'small' : 'large';
	const idFor = key => `${idPrefix}.${key}`;
	const isShort = dataType === 'text-small';
	const ceiling = maxLengthCeiling(dataType, options);
	const pattern = options.pattern && typeof options.pattern === 'object' ? options.pattern : null;
	const choice = patternChoiceOf(pattern);
	const preset = presetOf(choice);

	// maxLength: the stored value may sit past a ceiling that "Allow longer
	// text" no longer raises — said here, where it can be fixed.
	const maxLength = Number.isInteger(options.maxLength) ? options.maxLength : null;
	const maxLengthOver = maxLength !== null && ceiling !== null && (maxLength < 1 || maxLength > ceiling);
	const maxLengthError = (error?.option === 'maxLength' || maxLengthOver) ? maxLengthRangeText(dataType, options) : null;

	// A custom pattern, checked as it is typed — only once there is something
	// to check, so a fresh "Custom pattern…" does not open on a complaint.
	const local = choice === 'custom' && pattern && (pattern.regex || pattern.message) ? checkPattern(pattern) : null;
	const serverReason = error?.option === 'pattern' ? error.reason : null;
	const reason = (local && !local.ok ? local.reason : null) || serverReason;
	const regexReason = reason && reason !== 'message' ? reason : null;
	const normalize = value => storedTextValue(value, { dataType, options });
	const messageReason = reason === 'message' ? reason : null;
	const resolved = resolvePattern(pattern);

	const setPattern = next => onPatch({ pattern: next });
	const choose = next => {
		if (next === 'none') setPattern(undefined);
		else if (next === 'custom') setPattern({ regex: pattern?.regex || '', message: pattern?.message || '' });
		else setPattern({ preset: next });
	};

	const trialStored = trial ? storedTextValue(trial, { dataType, options }) : '';
	const trialFits = trial && resolved ? matchesPattern(trialStored, resolved) : null;

	return (
		<div className={`text-options${compact ? ' text-options--compact' : ''}`}>
			{compact ? null : <div className='micro-label card-def-element__section'>Text</div>}

			<div className='mb-2'>
				<label className='d-block' htmlFor={idFor('maxLength')}>Maximum length</label>
				<InputNumber
					id={idFor('maxLength')}
					size={size}
					className='w-100'
					min={1}
					max={ceiling || undefined}
					step={1}
					precision={0}
					value={maxLength}
					onChange={v => onPatch({ maxLength: Number.isInteger(v) && v > 0 ? v : undefined })}
					placeholder={ceiling ? formatLimit(ceiling) : ''}
					inputMode='numeric'
					status={maxLengthError ? 'error' : undefined}
					aria-label='Maximum length'
				/>
				<div className='form-panel__hint' role={maxLengthError ? 'alert' : undefined}>
					{maxLengthError || `The most characters a value may have. Empty: this type's limit, ${formatLimit(ceiling || 0)}.`}
				</div>
			</div>

			{isShort ? (
				<>
					<div className='mb-2'>
						<label className='d-block' id={idFor('case-label')}>Case</label>
						<Segmented
							size={size}
							aria-labelledby={idFor('case-label')}
							options={CASE_CHOICES}
							value={options.case === 'upper' || options.case === 'lower' ? options.case : ''}
							onChange={v => onPatch({ case: v || undefined })}
						/>
						<div className='form-panel__hint'>{CASE_HINTS[options.case === 'upper' || options.case === 'lower' ? options.case : '']}</div>
					</div>

					<div className='mb-2'>
						<label className='d-block' htmlFor={idFor('pattern')}>Pattern</label>
						<Select
							id={idFor('pattern')}
							size={size}
							className='w-100'
							value={choice}
							onChange={choose}
							aria-label='Pattern'
							virtual={false}
							status={regexReason && choice !== 'custom' ? 'error' : undefined}
							options={[
								{ value: 'none', label: 'None' },
								{ label: 'Presets', options: PRESETS.map(p => ({ value: p.id, label: p.label })) },
								{ value: 'custom', label: 'Custom pattern…' },
							]}
						/>
						<div className='form-panel__hint'>
							{choice === 'none' ? 'Any text. Pick a pattern to refuse values that do not have a shape — a ZIP code, a part number.' : null}
							{preset ? `${preset.hint ? `${preset.hint} ` : ''}A value that does not fit is told: “${resolved?.message}”` : null}
							{choice === 'custom' ? 'The whole value must match. Build it from pieces, or type examples of values that should fit and keepr proposes the pieces.' : null}
						</div>
					</div>

					{choice === 'custom' ? (
						<PatternEditor
							idPrefix={idPrefix}
							pattern={pattern}
							onChange={setPattern}
							serverReason={serverReason && serverReason !== 'message' ? serverReason : null}
							normalize={normalize}
							size={size}
							compact={compact}
						/>
					) : null}

					{choice !== 'none' ? (
						<div className='mb-2'>
							<label className='d-block' htmlFor={idFor('message')}>{choice === 'custom' ? 'Message' : 'Message (optional)'}</label>
							<Input
								id={idFor('message')}
								size={size}
								value={pattern?.message || ''}
								onChange={e => setPattern({ ...pattern, message: e.target.value || (choice === 'custom' ? '' : undefined) })}
								placeholder={preset ? preset.message : 'A plate looks like ABC-1234'}
								maxLength={MAX_MESSAGE}
								showCount
								status={messageReason ? 'error' : undefined}
								aria-label='Message'
							/>
							<div className='form-panel__hint' role={messageReason ? 'alert' : undefined}>
								{messageReason ? patternRefusalText(messageReason) : 'The one sentence a person reads when a value does not fit.'}
							</div>
						</div>
					) : null}

					{resolved && (choice !== 'custom' || (local && local.ok)) ? (
						<div className='mb-2'>
							<label className='d-block' htmlFor={idFor('trial')}>Try a value</label>
							<Input
								{...NO_TEXT_ASSIST}
								id={idFor('trial')}
								size={size}
								value={trial}
								onChange={e => setTrial(e.target.value)}
								placeholder='Type a value to check it'
								status={trial && !trialFits ? 'error' : undefined}
								aria-label='Try a value'
							/>
							{trial ? (
								<div className='form-panel__hint' data-testid='text-options-trial'>
									{trialFits
										? (trialStored !== trial ? `Matches. Saved as ${trialStored}.` : 'Matches.')
										: `Doesn’t match. A person is told: “${resolved.message}”`}
								</div>
							) : null}
						</div>
					) : null}
				</>
			) : null}
		</div>
	);
}
